// Email threads attached to a case (issue #681).
//
// The conversation with the customer usually holds the part of the story the
// structured outcome fields never capture. Attaching it here keeps it on the
// case, and the parsed text goes into the retrieval corpus when the case is
// archived — so future repairs get the benefit of it.

import React, { useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL;

const labelStyle = {
  fontWeight: 700,
  color: colors.textStrong,
  fontSize: '0.9rem',
  display: 'block',
  marginBottom: '0.35rem'
};

const rowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.6rem',
  padding: '0.55rem 0.7rem',
  borderRadius: 8,
  border: `1px solid ${colors.panelBorder}`,
  background: 'rgba(255,255,255,0.04)',
  marginBottom: '0.4rem'
};

function formatSent(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export default function RmaCaseEmails({ rma, onChange }) {
  const { getToken } = useAuth();
  const fileRef = useRef(null);
  const [emails, setEmails] = useState(rma?.caseEmails || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const form = new FormData();
      files.forEach(f => form.append('emails', f));
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/emails`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.message || 'Could not attach that email.');
        return;
      }
      setEmails(data.caseEmails || []);
      if (onChange) onChange(data.caseEmails || []);
      const skipped = (data.rejected || []).length;
      setNote(skipped
        ? `Attached ${data.added}. Skipped ${skipped} that could not be read as email.`
        : `Attached ${data.added}.`);
    } catch (err) {
      setError('Could not attach that email.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id) => {
    setBusy(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/emails/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError('Could not remove that email.');
        return;
      }
      setEmails(data.caseEmails || []);
      if (onChange) onChange(data.caseEmails || []);
    } catch (err) {
      setError('Could not remove that email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <label style={labelStyle}>
        Email thread <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
      </label>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.5 }}>
        Attach the correspondence for this case as <strong>.eml</strong>. In Outlook, drag the
        conversation onto your desktop, or use File → Save As and pick
        &quot;Outlook Message Format – Unicode (*.eml)&quot;. The text is read out and kept with the
        case, and feeds the repair suggestions on future cases.
      </p>

      {emails.length > 0 && (
        <div style={{ marginBottom: '0.6rem' }}>
          {emails.map(email => (
            <div key={email._id || email.url} style={rowStyle}>
              <span style={{ fontSize: '1rem', lineHeight: 1.3 }}>✉</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.textStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {email.subject || email.name || 'Email'}
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[email.from, formatSent(email.sentAt)].filter(Boolean).join(' · ')}
                  {email.truncated ? ' · long thread, stored in part' : ''}
                </div>
              </div>
              {email.url && (
                <a
                  href={email.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '0.75rem', color: colors.primary, textDecoration: 'none', flexShrink: 0 }}
                >
                  Open
                </a>
              )}
              <button
                type="button"
                onClick={() => remove(email._id)}
                disabled={busy}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: busy ? 'wait' : 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
                aria-label={`Remove ${email.subject || 'email'}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".eml"
        multiple
        disabled={busy}
        onChange={e => upload(e.target.files)}
        style={{ width: '100%', padding: '0.5rem 0.65rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.85rem', boxSizing: 'border-box' }}
      />

      {busy && <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: colors.textMuted }}>Reading the email…</p>}
      {note && <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: colors.primary }}>{note}</p>}
      {error && <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: colors.danger }}>{error}</p>}
    </div>
  );
}
